/**
 * Investors page - browse, search, and filter the investor intelligence library.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Loader2,
  UserPlus,
  Users,
  Search,
  MapPin,
  Briefcase,
  Tag as TagIcon,
  Upload as UploadIcon,
  Sparkles,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import DualScoreChip from '../components/DualScoreChip';
import EntityTableView, { TableColumn } from '../components/EntityTableView';
import investorService from '../services/investorService';
import dataService, { PlatformStats } from '../services/data';
import api from '../services/api';
import {
  Investor,
  InvestorListFilters,
  InvestorType,
  Stage,
  AccreditationStatus,
  RelationshipStatus,
} from '../types/investor';

const INVESTOR_TYPE_OPTIONS: Array<{ value: InvestorType; label: string }> = [
  { value: 'angel', label: 'Angel' },
  { value: 'super_angel', label: 'Super Angel' },
  { value: 'micro_vc', label: 'Micro VC' },
  { value: 'traditional_vc', label: 'Traditional VC' },
  { value: 'family_office', label: 'Family Office' },
  { value: 'corporate_strategic', label: 'Corporate Strategic' },
  { value: 'hnw_individual', label: 'HNW Individual' },
  { value: 'syndicate_lead', label: 'Syndicate Lead' },
  { value: 'lp_fof', label: 'LP / Fund of Funds' },
  { value: 'lp_institutional', label: 'LP — Institutional (pension/endowment/SWF)' },
  { value: 'lp_individual', label: 'LP — Individual / HNW' },
  { value: 'philanthropy', label: 'Philanthropy / Foundation' },
  { value: 'corporate_philanthropy', label: 'Corporate Philanthropy' },
];

const STAGE_OPTIONS: Array<{ value: Stage; label: string }> = [
  { value: 'pre_seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'series_c', label: 'Series C' },
  { value: 'growth', label: 'Growth' },
  { value: 'late_stage', label: 'Late Stage' },
];

const ACCREDITATION_OPTIONS: Array<{ value: AccreditationStatus; label: string }> = [
  { value: 'confirmed_accredited', label: 'Confirmed Accredited' },
  { value: 'likely_high', label: 'Likely (High)' },
  { value: 'likely_medium', label: 'Likely (Medium)' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'non_accredited', label: 'Non-Accredited' },
];

const RELATIONSHIP_OPTIONS: Array<{ value: RelationshipStatus; label: string }> = [
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'dormant', label: 'Dormant' },
];

const accreditationBadgeClasses = (status?: AccreditationStatus): string => {
  switch (status) {
    case 'confirmed_accredited':
      return 'bg-green-100 text-green-800';
    case 'likely_high':
      return 'bg-emerald-100 text-emerald-800';
    case 'likely_medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'non_accredited':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatLabel = (value?: string): string => {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDate = (value?: string): string => {
  if (!value) return 'No contact yet';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const formatLocation = (investor: Investor): string => {
  return [investor.location_city, investor.location_state, investor.location_country]
    .filter(Boolean)
    .join(', ');
};

// Freshness rules — compares last_researched_at against age buckets so
// the operator can see at a glance whether a profile is up to date.
const STALE_DAYS = 14;       // amber after 14 days
const VERY_STALE_DAYS = 30;  // red after 30 days

interface Freshness {
  label: string;
  color: string;
  needsUpdate: boolean;
}

const freshnessFor = (lastResearchedAt?: string | null): Freshness => {
  if (!lastResearchedAt) {
    return {
      label: 'Not assessed',
      color: 'bg-gray-100 text-gray-700 border-gray-200',
      needsUpdate: true,
    };
  }
  let ageDays = 0;
  try {
    ageDays = Math.floor(
      (Date.now() - new Date(lastResearchedAt).getTime()) / (1000 * 60 * 60 * 24),
    );
  } catch {
    ageDays = 999;
  }
  const dateStr = (() => {
    try { return new Date(lastResearchedAt).toLocaleDateString(); } catch { return ''; }
  })();
  if (ageDays >= VERY_STALE_DAYS) {
    return {
      label: `Assessed ${dateStr} · update needed`,
      color: 'bg-red-50 text-red-700 border-red-200',
      needsUpdate: true,
    };
  }
  if (ageDays >= STALE_DAYS) {
    return {
      label: `Assessed ${dateStr} · ${ageDays}d old`,
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      needsUpdate: true,
    };
  }
  if (ageDays === 0) {
    return {
      label: 'Assessed today',
      color: 'bg-green-50 text-green-700 border-green-200',
      needsUpdate: false,
    };
  }
  return {
    label: `Assessed ${ageDays}d ago`,
    color: 'bg-green-50 text-green-700 border-green-200',
    needsUpdate: false,
  };
};

const InvestorsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchInput, setSearchInput] = useState<string>('');
  // Pre-populate from the URL — Philanthropies sidebar entry sets
  // ?investor_type=philanthropy and we want the page to land on that
  // filter view without an extra click.
  const _initialType = searchParams.get('investor_type') as InvestorType | null;
  const [filters, setFilters] = useState<InvestorListFilters>(
    _initialType ? { investor_type: _initialType } : {},
  );
  const isPhilanthropyView = !!(filters.investor_type
    && (filters.investor_type === 'philanthropy'
        || filters.investor_type === 'corporate_philanthropy'));
  const isFunderView = !!(filters.investor_type
    && (filters.investor_type === 'lp_fof'
        || filters.investor_type === 'lp_institutional'
        || filters.investor_type === 'lp_individual'));
  // By default, hide thinly-researched investors from the main list.
  // Operators can toggle off to see everyone. Search bypasses regardless.
  const [showAll, setShowAll] = useState<boolean>(false);
  const MIN_COMPLETION_DEFAULT = 60;
  const [minCompletion, setMinCompletion] = useState<number | null>(null);
  const [sort, setSort] = useState<string>('completion_desc');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(
    (localStorage.getItem('investorsViewMode') as 'cards' | 'table') || 'cards',
  );
  useEffect(() => {
    localStorage.setItem('investorsViewMode', viewMode);
  }, [viewMode]);
  const [platform, setPlatform] = useState<PlatformStats | null>(null);
  const [autoResearchRunning, setAutoResearchRunning] = useState<boolean>(false);
  const [autoResearchProgress, setAutoResearchProgress] = useState<{
    enrolled: number; queueSize: number;
  } | null>(null);

  const refreshPlatformStats = async () => {
    const res = await dataService.fetchPlatformStats();
    if (res.data) setPlatform(res.data);
  };

  useEffect(() => {
    refreshPlatformStats();
  }, []);

  /**
   * Loop /investors/research/bulk-enqueue?limit=100 until enrolled=0.
   * Updates progress in the toast + button label as it goes.
   */
  const handleAutoResearch = async () => {
    const remaining = platform
      ? platform.investors.total - platform.investors.researched
      : 0;
    if (remaining <= 0) {
      toast.success('Every investor is already researched.');
      return;
    }
    if (!window.confirm(
      `Enroll ${remaining} unresearched investor(s) in the research pipeline?\n\n` +
      `This runs all free-tier services (Crunchbase, Google News, GitHub, ` +
      `PACER, USPTO, etc.) in batches of 100. The Claude dossier stage is ` +
      `skipped only if the backend has RESEARCH_SKIP_CLAUDE=true set.`
    )) {
      return;
    }

    setAutoResearchRunning(true);
    setAutoResearchProgress({ enrolled: 0, queueSize: 0 });
    let totalEnrolled = 0;
    let totalQueued = 0;
    const t = toast.loading('Enrolling investors for research…');

    try {
      // Hard cap of 30 iterations × 100 = 3,000 investors. Plenty of
      // headroom over the current ~1.5K and prevents an infinite loop
      // if the endpoint ever returns the same enrolled count repeatedly.
      for (let i = 0; i < 30; i++) {
        const resp = await api.post(
          '/investors/research/bulk-enqueue?limit=100',
        );
        const enrolled = Number(resp.data?.enrolled || 0);
        const queued = Number(resp.data?.queued || 0);
        const queueSize = Number(resp.data?.queue_size || 0);
        totalEnrolled += enrolled;
        totalQueued += queued;
        setAutoResearchProgress({ enrolled: totalEnrolled, queueSize });
        toast.loading(
          `Enrolled ${totalEnrolled} so far (queue: ${queueSize})…`,
          { id: t },
        );
        if (enrolled === 0) break;
        // Brief pause so the queue doesn't get hammered
        await new Promise((r) => setTimeout(r, 500));
      }
      toast.success(
        `Done — enrolled ${totalEnrolled} investor(s), queued ${totalQueued}.`,
        { id: t },
      );
      await refreshPlatformStats();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail || 'Bulk research enroll failed.',
        { id: t },
      );
    } finally {
      setAutoResearchRunning(false);
    }
  };

  const PAGE_SIZE = 50;
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const queryFilters: InvestorListFilters = useMemo(
    () => ({
      size: PAGE_SIZE,
      page,
      ...filters,
      // Backend bypasses the gate when search is active, but pass it
      // anyway so the wire is consistent.
      min_completion:
        minCompletion !== null
          ? minCompletion
          : showAll
            ? 0
            : MIN_COMPLETION_DEFAULT,
      sort,
    }),
    [filters, page, showAll, minCompletion, sort],
  );

  // Reset to page 1 whenever filters change (search, type, sector, etc).
  useEffect(() => {
    setPage(1);
  }, [filters, showAll, minCompletion, sort]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const result = await investorService.fetchInvestors(queryFilters);
      if (result.error) {
        toast.error(result.error);
        setInvestors([]);
        setTotalPages(1);
        setTotalCount(0);
      } else {
        setInvestors(result.data?.investors || []);
        setTotalPages(result.data?.pages || 1);
        setTotalCount(result.data?.total || 0);
      }
      setIsLoading(false);
    };

    loadData();
  }, [queryFilters]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput || undefined }));
  };

  const handleFilterChange = <K extends keyof InvestorListFilters>(
    key: K,
    value: InvestorListFilters[K] | ''
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isPhilanthropyView
                ? 'Philanthropies'
                : isFunderView
                  ? 'VC Funders'
                  : 'Investors'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isPhilanthropyView
                ? 'Grant-making foundations + corporate philanthropy arms — match by mission alignment.'
                : isFunderView
                  ? 'LPs that fund VC vehicles — pensions, endowments, family offices, fund-of-funds, individual HNW LPs.'
                  : 'Investor intelligence library — search, filter, and review profiles.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAutoResearch}
              disabled={autoResearchRunning}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              title="Run free-tier research (Crunchbase, Google News, GitHub, PACER, USPTO, etc.) on every unresearched investor"
            >
              {autoResearchRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enrolling
                  {autoResearchProgress
                    ? ` (${autoResearchProgress.enrolled})`
                    : '…'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Auto-research all
                </>
              )}
            </button>
            <Link
              to="/imports/new"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Import investors
            </Link>
            <Link
              to="/investors/upload-brochure"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              title="Upload a firm tearsheet / fund overview — Claude extracts the investor profile"
            >
              <UploadIcon className="w-4 h-4 mr-2" />
              Upload brochure
            </Link>
            <Link
              to="/investors/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add investor
            </Link>
          </div>
        </div>

        {/* Stats card */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            type="button"
            onClick={() => { setFilters({}); setSearchInput(''); setPage(1); }}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="Show all investors (clears filters)"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.investors.total ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Total investors</p>
          </button>
          <button
            type="button"
            onClick={() => { setFilters({}); setSearchInput(''); setPage(1); }}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="Researched investors (filtering by research status coming soon)"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.investors.researched ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">
              Researched
              {platform && platform.investors.total > 0 && (
                <span className="ml-1 text-gray-400">
                  ({Math.round((platform.investors.researched / platform.investors.total) * 100)}%)
                </span>
              )}
            </p>
          </button>
          <Link
            to="/matches"
            className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="View all matches"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.investors.matched ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Matched to a deal</p>
          </Link>
          <Link
            to="/engagements"
            className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
            title="View outreach engagements"
          >
            <p className="text-2xl font-bold text-gray-900">{platform?.investors.with_outreach ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-1">Outreach sent</p>
          </Link>
        </div>

        {/* Search + filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <form onSubmit={handleSearchSubmit} className="flex items-center flex-wrap gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or firm..."
                className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              Search
            </button>
            <label
              className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer whitespace-nowrap select-none"
              title={`Default view hides investors below ${MIN_COMPLETION_DEFAULT}% research completion. Search bypasses this gate.`}
            >
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="rounded"
              />
              Show all (incl. unassessed)
            </label>
            <select
              value={minCompletion ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setMinCompletion(v === '' ? null : Number(v));
              }}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              title="Filter by completion score range"
            >
              <option value="">Any completion</option>
              <option value="0">≥ 0%</option>
              <option value="50">≥ 50%</option>
              <option value="60">≥ 60%</option>
              <option value="70">≥ 70%</option>
              <option value="80">≥ 80%</option>
              <option value="90">≥ 90%</option>
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              title="Sort the list"
            >
              <option value="completion_desc">Sort: Completion (high → low)</option>
              <option value="completion_asc">Sort: Completion (low → high)</option>
              <option value="name_asc">Sort: Name (A → Z)</option>
              <option value="name_desc">Sort: Name (Z → A)</option>
              <option value="last_investment_recent">Sort: Most-recent investment</option>
              <option value="investment_frequency_desc">Sort: Most active (last 12 mo)</option>
              <option value="researched_recent">Sort: Recently researched</option>
              <option value="recent">Sort: Recently added</option>
              <option value="oldest">Sort: Oldest first</option>
            </select>
            <div className="inline-flex items-center rounded-md border border-gray-200 bg-white overflow-hidden text-xs">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`px-2.5 py-1.5 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                title="Card view"
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-2.5 py-1.5 ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                title="Excel-style table view"
              >
                Table
              </button>
            </div>
          </form>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.investor_type || ''}
              onChange={(e) =>
                handleFilterChange('investor_type', e.target.value as InvestorType | '')
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All types</option>
              {INVESTOR_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.stage || ''}
              onChange={(e) => handleFilterChange('stage', e.target.value as Stage | '')}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All stages</option>
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.accreditation_status || ''}
              onChange={(e) =>
                handleFilterChange(
                  'accreditation_status',
                  e.target.value as AccreditationStatus | ''
                )
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All accreditation</option>
              {ACCREDITATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={filters.relationship_status || ''}
              onChange={(e) =>
                handleFilterChange(
                  'relationship_status',
                  e.target.value as RelationshipStatus | ''
                )
              }
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All relationships</option>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Body states */}
        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading investors...
          </div>
        ) : investors.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
            <Users className="w-10 h-10 mx-auto text-blue-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">No investors found</h2>
            <p className="text-sm">
              Try adjusting filters, or add your first investor profile.
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <EntityTableView
            rows={investors}
            csvFilename={isPhilanthropyView ? 'philanthropies.csv' : 'investors.csv'}
            onRowClick={(inv) => navigate(`/investors/${(inv as Investor).id}`)}
            columns={[
              { key: 'full_name', label: 'Name' },
              { key: 'firm_name', label: 'Firm' },
              {
                key: 'investor_type',
                label: 'Type',
                render: (r: Investor) => formatLabel(r.investor_type) || '—',
                csv: (r: Investor) => r.investor_type || '',
              },
              {
                key: 'completion',
                label: 'Completion',
                align: 'right',
                render: (r: Investor) => `${r.research_completion_pct ?? 0}%`,
                csv: (r: Investor) => r.research_completion_pct ?? 0,
                // `key` is a display id, not a field name — without this the
                // table would sort on row.completion (undefined) and silently
                // no-op. Same for every column below whose key !== field.
                sortValue: (r: Investor) => r.research_completion_pct ?? null,
              },
              {
                key: 'rubric',
                label: 'Rubric',
                align: 'right',
                render: (r: Investor) =>
                  r.rubric_score !== undefined && r.rubric_score !== null
                    ? `${r.rubric_score} (${r.rubric_tier || ''})`
                    : '—',
                csv: (r: Investor) => r.rubric_score ?? '',
                sortValue: (r: Investor) => r.rubric_score ?? null,
              },
              {
                key: 'sectors',
                label: 'Sectors / Mission',
                render: (r: Investor) =>
                  ((r.sector_focus && r.sector_focus.length
                    ? r.sector_focus
                    : (r as any).mission_areas) || []).join(', ') || '—',
                csv: (r: Investor) =>
                  ((r.sector_focus && r.sector_focus.length
                    ? r.sector_focus
                    : (r as any).mission_areas) || []).join('; '),
                sortValue: (r: Investor) =>
                  ((r.sector_focus && r.sector_focus.length
                    ? r.sector_focus
                    : (r as any).mission_areas) || []).join(', ') || null,
              },
              {
                key: 'location',
                label: 'Location',
                render: (r: Investor) =>
                  [r.location_city, r.location_state].filter(Boolean).join(', ') || '—',
              },
              {
                key: 'last_researched_at',
                label: 'Last researched',
                render: (r: Investor) =>
                  r.last_researched_at
                    ? new Date(r.last_researched_at).toLocaleDateString()
                    : '—',
              },
            ]}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {investors.map((investor) => (
              <button
                key={investor.id}
                onClick={() => navigate(`/investors/${investor.id}`)}
                className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-md transition-all space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-lg font-semibold text-gray-900 truncate">
                        {investor.full_name}
                      </h2>
                      <DualScoreChip
                        completionPct={investor.research_completion_pct ?? null}
                        rubricScore={investor.rubric_score ?? null}
                        rubricTier={investor.rubric_tier ?? null}
                      />
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {[investor.title, investor.firm_name].filter(Boolean).join(' • ') ||
                        'No firm/title on file'}
                    </p>
                  </div>
                  {investor.accreditation_status && (
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${accreditationBadgeClasses(
                        investor.accreditation_status
                      )}`}
                    >
                      {formatLabel(investor.accreditation_status)}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                  {investor.investor_type && (
                    <span className="inline-flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                      <Briefcase className="w-3 h-3 mr-1" />
                      {formatLabel(investor.investor_type)}
                    </span>
                  )}
                  {formatLocation(investor) && (
                    <span className="inline-flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {formatLocation(investor)}
                    </span>
                  )}
                </div>

                {investor.sector_focus && investor.sector_focus.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {investor.sector_focus.slice(0, 5).map((sector) => (
                      <span
                        key={sector}
                        className="inline-flex items-center text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded"
                      >
                        <TagIcon className="w-3 h-3 mr-1" />
                        {sector}
                      </span>
                    ))}
                    {investor.sector_focus.length > 5 && (
                      <span className="text-xs text-gray-500">
                        +{investor.sector_focus.length - 5} more
                      </span>
                    )}
                  </div>
                )}

                {/* Research-completion rubric progress bar */}
                {(() => {
                  const pct = investor.research_completion_pct ?? 0;
                  const barColor =
                    pct >= 80 ? 'bg-green-500' :
                    pct >= 50 ? 'bg-blue-500' :
                    pct >= 20 ? 'bg-amber-500' : 'bg-gray-300';
                  return (
                    <div title="Research completion (rubric of fields populated)">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Research completion</span>
                        <span className="font-medium text-gray-700">{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Assessment freshness badge — green/amber/red based on
                    how long ago the row was last researched. */}
                {(() => {
                  const f = freshnessFor(investor.last_researched_at);
                  return (
                    <div className={`text-[11px] font-medium border rounded px-2 py-1 ${f.color}`}>
                      {f.label}
                    </div>
                  );
                })()}

                {/* Investment activity — drives Aaron's "sort by recent
                    investment" + "frequency" surfaces. Only render when
                    we actually have portfolio data; suppress entirely
                    on investors with no investments on record so the
                    card doesn't grow with empty lines. */}
                {(investor.last_investment_date || (investor.recent_investment_count ?? 0) > 0) && (
                  <div className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded px-2 py-1">
                    <span>
                      Last invested:{' '}
                      <span className="font-medium text-gray-800">
                        {investor.last_investment_date
                          ? formatDate(investor.last_investment_date)
                          : '—'}
                      </span>
                    </span>
                    <span title="Investments in the trailing 12 months">
                      <span className="font-medium text-gray-800">
                        {investor.recent_investment_count ?? 0}
                      </span>{' '}
                      in last 12 mo
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
                  <span>Last contact: {formatDate(investor.last_contact_date)}</span>
                  {investor.relationship_status && (
                    <span className="font-medium text-gray-700">
                      {formatLabel(investor.relationship_status)}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
              –<span className="font-medium">{Math.min(page * PAGE_SIZE, totalCount)}</span>
              {' '}of <span className="font-medium">{totalCount.toLocaleString()}</span> investors
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                « First
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ‹ Prev
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-700">
                Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next ›
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last »
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default InvestorsPage;
